/**
 * Facturación por médico colaborador (2026-08-25).
 *
 * Entrada: tarjetas de médicos con lo pendiente. Al pinchar uno: su actividad
 * (tratamientos y cirugías) con los números que el sistema calcula solo.
 *  - Tratamiento: el médico rellena tratamiento + producto + cantidad
 *    (+ descuento opcional); PVP y coste salen de los catálogos.
 *  - Cirugía: facturación mete precio + anestesia + instrumentista; los
 *    fungibles fijos y el reparto los pone el sistema con la configuración.
 *
 * Los catálogos (tratamientos, productos, cirugías) y los porcentajes son de
 * cada clínica: viven en la base, empiezan VACÍOS y gestión los edita aquí
 * mismo («Catálogos» y «Porcentajes y fungibles»). Mientras falten, la
 * pantalla lo avisa en vez de inventarse un tarifario o un reparto.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, Banknote, Check, ChevronRight, ListChecks, Loader2, Pencil, Plus, Settings2,
  Stethoscope, Syringe, Trash2, X,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import ColabActividadDialog from '../components/ColabActividadDialog';
import { PageHeader } from '../components/Common';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const eur = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');
const hoyISO = () => new Date().toISOString().slice(0, 10);

const ROLES_GESTION = ['admin', 'direccion', 'administrativo'];

export default function ColaboradoresPage() {
  const { user } = useAuth();
  const esGestion = ROLES_GESTION.includes((user?.role || '').toLowerCase());
  const [resumen, setResumen] = useState(null);
  const [medico, setMedico] = useState(null);        // {medico_id, medico_nombre}
  const [detalle, setDetalle] = useState(null);      // {items, totales}
  const [catalogos, setCatalogos] = useState(null);
  const [medicos, setMedicos] = useState([]);
  const [dialogo, setDialogo] = useState(null);      // 'tratamiento' | 'cirugia' | 'config' | 'catalogos'

  const cargarResumen = useCallback(() => {
    if (!esGestion) return;
    api.get('/api/colaboradores/resumen')
      .then((r) => setResumen(r.data?.medicos || []))
      .catch(() => setResumen([]));
  }, [esGestion]);

  const cargarDetalle = useCallback((m) => {
    const params = m?.medico_id ? { medico_id: m.medico_id } : {};
    api.get('/api/colaboradores/actividades', { params })
      .then((r) => setDetalle(r.data))
      .catch(() => setDetalle({ items: [], totales: {} }));
  }, []);

  const cargarCatalogos = useCallback(() => {
    api.get('/api/colaboradores/catalogos').then((r) => setCatalogos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    cargarCatalogos();
    if (esGestion) {
      cargarResumen();
      api.get('/api/colaboradores/medicos-posibles').then((r) => setMedicos(r.data?.medicos || [])).catch(() => {});
    } else {
      // Un médico entra directo a lo suyo
      const yo = { medico_id: user?.id, medico_nombre: user?.name };
      setMedico(yo); cargarDetalle(yo);
    }
  }, [esGestion, cargarResumen, cargarDetalle, cargarCatalogos, user]);

  // Lo que falta configurar para poder registrar actos (sin defectos en el
  // código: cada clínica pone sus catálogos y sus porcentajes).
  const pendientes = useMemo(() => {
    if (!catalogos) return [];
    const falta = [];
    const cfg = catalogos.config || {};
    if (cfg.pct_medico_tratamiento == null || cfg.pct_medico_cirugia == null) falta.push('los porcentajes del reparto');
    if (!(catalogos.tratamientos || []).length) falta.push('el catálogo de tratamientos');
    if (!(catalogos.productos || []).length) falta.push('el catálogo de productos');
    if (!(catalogos.cirugias || []).length) falta.push('el catálogo de cirugías');
    return falta;
  }, [catalogos]);

  const abrirMedico = (m) => { setMedico(m); cargarDetalle(m); };
  const volver = () => { setMedico(null); setDetalle(null); cargarResumen(); };

  const marcarPagado = async (a) => {
    try {
      await api.post(`/api/colaboradores/actividades/${a.id}/pagado`, null, { params: { pagado: !a.pagado } });
      cargarDetalle(medico);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo actualizar'); }
  };
  const borrar = async (a) => {
    try { await api.delete(`/api/colaboradores/actividades/${a.id}`); cargarDetalle(medico); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo borrar'); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Colaboradores"
        subtitle="Facturación por médico: tratamientos y cirugías, con lo que la clínica le debe a cada uno."
      />

      {esGestion && !medico && pendientes.length > 0 && (
        <Card className="cp-card p-3 flex items-start gap-2 text-sm text-[#8a5a12] bg-[#fff8eb]" data-testid="colab-falta-configurar">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Falta configurar {pendientes.join(', ')}. Complétalo en «Catálogos» y «Porcentajes y fungibles»
            para poder registrar actos.</span>
        </Card>
      )}

      {esGestion && !medico && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {(resumen || []).map((m) => (
              <button key={m.medico_id} type="button" onClick={() => abrirMedico(m)}
                className="cp-card p-4 text-left hover:ring-1 hover:ring-[#2E6E5E]/40 transition-[box-shadow]"
                data-testid={`colab-medico-${m.medico_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#13231E] truncate">{m.medico_nombre || '(sin nombre)'}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {m.tratamientos} tratamientos · {m.cirugias} cirugías
                      {m.ultima_fecha ? ` · última ${m.ultima_fecha}` : ''}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pendiente</div>
                    <div className="text-xl font-bold text-[#8a5a12] tabular-nums">{eur(m.pendiente)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ya pagado</div>
                    <div className="text-sm font-medium text-muted-foreground tabular-nums">{eur(m.pagado)}</div>
                  </div>
                </div>
              </button>
            ))}
            {resumen && resumen.length === 0 && (
              <Card className="cp-card p-6 text-sm text-muted-foreground sm:col-span-2 xl:col-span-3">
                Aún no hay actividad registrada. Añade la primera con los botones de abajo.
              </Card>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setDialogo('tratamiento')} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-nuevo-tratamiento">
              <Syringe className="w-4 h-4 mr-2" /> Añadir tratamiento
            </Button>
            <Button onClick={() => setDialogo('cirugia')} variant="outline" data-testid="colab-nueva-cirugia">
              <Stethoscope className="w-4 h-4 mr-2" /> Añadir cirugía
            </Button>
            <Button onClick={() => setDialogo('catalogos')} variant="ghost" data-testid="colab-catalogos">
              <ListChecks className="w-4 h-4 mr-2" /> Catálogos
            </Button>
            <Button onClick={() => setDialogo('config')} variant="ghost" data-testid="colab-config">
              <Settings2 className="w-4 h-4 mr-2" /> Porcentajes y fungibles
            </Button>
          </div>
        </>
      )}

      {medico && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {esGestion && (
                <Button variant="ghost" size="sm" onClick={volver} data-testid="colab-volver">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Médicos
                </Button>
              )}
              <h2 className="text-lg font-semibold text-[#13231E]">{medico.medico_nombre}</h2>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setDialogo('tratamiento')} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]">
                <Plus className="w-4 h-4 mr-1" /> Tratamiento
              </Button>
              {/* Cirugía aquí solo GESTIÓN (2026-08-26): este diálogo no lleva
                  paciente asociado y el servidor exige cita de quirófano del
                  paciente ese día para doctores — desde aquí siempre daba
                  error. El cirujano colaborador registra las suyas desde el
                  EPISODIO del día de quirófano, que sí lleva el paciente clavado. */}
              {esGestion && (
                <Button size="sm" variant="outline" onClick={() => setDialogo('cirugia')}>
                  <Plus className="w-4 h-4 mr-1" /> Cirugía
                </Button>
              )}
            </div>
          </div>

          {detalle && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[['Neto total', detalle.totales?.neto], ['Su parte', detalle.totales?.parte_medico],
                ['Parte clínica', detalle.totales?.parte_clinica], ['PENDIENTE de pagarle', detalle.totales?.pendiente]]
                .map(([t, v]) => (
                  <Card key={t} className="cp-card p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t}</div>
                    <div className={`text-lg font-bold tabular-nums ${t.startsWith('PEND') ? 'text-[#8a5a12]' : 'text-[#13231E]'}`}>{eur(v)}</div>
                  </Card>
                ))}
            </div>
          )}

          <Card className="cp-card p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground text-left border-b border-border">
                  <th className="py-2 px-3">Fecha</th>
                  <th className="py-2 px-3">Paciente</th>
                  <th className="py-2 px-3">Qué</th>
                  <th className="py-2 px-3 text-right">PVP sin IVA</th>
                  <th className="py-2 px-3 text-right">Costes</th>
                  <th className="py-2 px-3 text-right">Neto</th>
                  <th className="py-2 px-3 text-right">Médico</th>
                  <th className="py-2 px-3 text-right">Clínica</th>
                  <th className="py-2 px-3 text-center">Pagado</th>
                  {esGestion && <th className="py-2 px-1" />}
                </tr>
              </thead>
              <tbody>
                {(detalle?.items || []).map((a) => (
                  <tr key={a.id} className={`border-b border-border/60 ${a.pagado ? 'opacity-60' : ''}`} data-testid={`colab-fila-${a.id}`}>
                    <td className="py-2 px-3 whitespace-nowrap tabular-nums">{a.fecha}</td>
                    <td className="py-2 px-3">{a.paciente_nombre}</td>
                    <td className="py-2 px-3">
                      {a.tipo === 'tratamiento'
                        ? <>{a.tratamiento} <span className="text-xs text-muted-foreground">· {(a.productos?.length ? a.productos.map((l) => `${l.producto}${l.cantidad > 1 ? ` ×${l.cantidad}` : ''}`).join(' + ') : `${a.producto || ''}${a.cantidad > 1 ? ` ×${a.cantidad}` : ''}`)}{a.descuento_pct ? ` · dto ${a.descuento_pct}%` : ''}</span></>
                        : <>{a.intervencion} <span className="text-xs text-muted-foreground">· cirugía</span></>}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{eur(a.pvp_sin_iva)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">
                      {a.tipo === 'tratamiento' ? eur(a.coste_producto_total) : eur(a.coste_total)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-medium">{eur(a.neto)}</td>
                    <td className="py-2 px-3 text-right tabular-nums font-semibold text-[#1E4A3F]">{eur(a.parte_medico)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{eur(a.parte_clinica)}</td>
                    <td className="py-2 px-3 text-center">
                      {esGestion ? (
                        <button type="button" onClick={() => marcarPagado(a)}
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition-colors ${
                            a.pagado ? 'bg-[#2E6E5E] border-[#2E6E5E] text-white' : 'border-border text-transparent hover:border-[#2E6E5E]'}`}
                          title={a.pagado ? `Pagado${a.pagado_por ? ` por ${a.pagado_por}` : ''} — pulsar para deshacer` : 'Marcar como pagado'}
                          data-testid={`colab-pagado-${a.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      ) : (a.pagado ? <Check className="w-4 h-4 text-[#2E6E5E] inline" /> : '—')}
                    </td>
                    {esGestion && (
                      <td className="py-2 px-1">
                        <button type="button" onClick={() => borrar(a)} className="text-muted-foreground hover:text-red-700" title="Borrar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {detalle && detalle.items.length === 0 && (
                  <tr><td colSpan={10} className="py-6 px-3 text-center text-sm text-muted-foreground">Sin actividad todavía.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </>
      )}

      <ColabActividadDialog
        tipo={dialogo === 'tratamiento' || dialogo === 'cirugia' ? dialogo : null}
        onClose={() => setDialogo(null)}
        catalogos={catalogos}
        medicos={medicos}
        esGestion={esGestion}
        medicoActual={medico}
        onCreada={() => { setDialogo(null); if (medico) cargarDetalle(medico); else cargarResumen(); }}
      />
      {dialogo === 'catalogos' && catalogos && (
        <DialogoCatalogos catalogos={catalogos} onClose={() => setDialogo(null)} onCambio={cargarCatalogos} />
      )}
      {dialogo === 'config' && catalogos && (
        <DialogoConfig config={catalogos.config} onClose={() => setDialogo(null)}
          onGuardada={(cfg) => { setCatalogos((c) => ({ ...c, config: cfg })); setDialogo(null); }} />
      )}
    </div>
  );
}

function DialogoConfig({ config, onClose, onGuardada }) {
  const [f, setF] = useState({
    // Sin valores inventados: lo que no está configurado sale en blanco.
    pct_medico_tratamiento: config?.pct_medico_tratamiento ?? '',
    pct_medico_cirugia: config?.pct_medico_cirugia ?? '',
    fungibles_defecto: config?.fungibles_defecto ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const guardar = async () => {
    if (['pct_medico_tratamiento', 'pct_medico_cirugia'].some((k) => String(f[k]).trim() === '')) {
      toast.error('Pon los dos porcentajes del médico');
      return;
    }
    setGuardando(true);
    try {
      const r = await api.put('/api/colaboradores/config', {
        pct_medico_tratamiento: Number(f.pct_medico_tratamiento),
        pct_medico_cirugia: Number(f.pct_medico_cirugia),
        fungibles_defecto: Number(f.fungibles_defecto),
      });
      toast.success('Configuración guardada');
      onGuardada(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Porcentajes y fungibles</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {[['% del médico en tratamientos', 'pct_medico_tratamiento'],
            ['% del médico en cirugías', 'pct_medico_cirugia'],
            ['Fungibles fijos por cirugía (€)', 'fungibles_defecto']].map(([lbl, k]) => (
              <div key={k}>
                <Label>{lbl}</Label>
                <Input inputMode="decimal" value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} />
              </div>
          ))}
          <Button onClick={guardar} disabled={guardando} className="w-full bg-[#2E6E5E] hover:bg-[#1E4A3F]">
            {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Los tres catálogos de la clínica, editables por gestión. Cada uno con su
// precio: el PVP del tratamiento, el coste por unidad del producto y el
// precio orientativo de la cirugía (con la marca de si va sin IVA).
const CATALOGOS = [
  { clave: 'tratamientos', titulo: 'Tratamientos', campo: 'pvp', precio: 'PVP (€)' },
  { clave: 'productos', titulo: 'Productos', campo: 'coste_vial', precio: 'Coste por unidad (€)' },
  { clave: 'cirugias', titulo: 'Cirugías', campo: 'sugerido', precio: 'Precio orientativo (€)' },
];

function DialogoCatalogos({ catalogos, onClose, onCambio }) {
  const [cual, setCual] = useState('tratamientos');
  const [editando, setEditando] = useState(null);   // {id?, nombre, precio, sin_iva}
  const [guardando, setGuardando] = useState(false);
  const def = CATALOGOS.find((c) => c.clave === cual);
  const items = catalogos?.[cual] || [];

  const guardar = async () => {
    const nombre = String(editando?.nombre || '').trim();
    const precio = Number(String(editando?.precio ?? '').replace(',', '.'));
    if (!nombre || !Number.isFinite(precio) || precio < 0) {
      toast.error('Pon el nombre y un precio válido');
      return;
    }
    const cuerpo = { nombre, precio, ...(cual === 'cirugias' ? { sin_iva: !!editando.sin_iva } : {}) };
    setGuardando(true);
    try {
      if (editando.id) await api.put(`/api/colaboradores/catalogos/${cual}/${editando.id}`, cuerpo);
      else await api.post(`/api/colaboradores/catalogos/${cual}`, cuerpo);
      setEditando(null);
      onCambio();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  };
  const quitar = async (it) => {
    if (!window.confirm(`¿Quitar «${it.nombre}» del catálogo? Los actos ya registrados no cambian.`)) return;
    try { await api.delete(`/api/colaboradores/catalogos/${cual}/${it.id}`); onCambio(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo quitar'); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Catálogos de colaboradores</DialogTitle></DialogHeader>
        <div className="flex gap-1 mb-2" role="tablist">
          {CATALOGOS.map((c) => (
            <Button key={c.clave} size="sm" variant={cual === c.clave ? 'default' : 'outline'}
              className={cual === c.clave ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F]' : ''}
              onClick={() => { setCual(c.clave); setEditando(null); }} data-testid={`colab-cat-${c.clave}`}>
              {c.titulo} <span className="ml-1 text-xs opacity-70">{(catalogos?.[c.clave] || []).length}</span>
            </Button>
          ))}
        </div>
        <div className="space-y-1">
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">Vacío. Añade la primera entrada.</p>
          )}
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-2 border-b border-border/60 py-1.5 text-sm"
              data-testid={`colab-cat-fila-${it.id}`}>
              <span className="min-w-0 truncate">{it.nombre}{cual === 'cirugias' && it.sin_iva ? ' (sin IVA)' : ''}</span>
              <span className="flex items-center gap-1 shrink-0">
                <span className="tabular-nums">{eur(it[def.campo])}</span>
                <button type="button" className="p-1 text-muted-foreground hover:text-[#13231E]" title="Editar"
                  onClick={() => setEditando({ id: it.id, nombre: it.nombre, precio: it[def.campo], sin_iva: !!it.sin_iva })}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" className="p-1 text-muted-foreground hover:text-red-700" title="Quitar"
                  onClick={() => quitar(it)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </span>
            </div>
          ))}
        </div>
        {editando ? (
          <div className="mt-3 space-y-2 border border-border rounded-md p-3" data-testid="colab-cat-editor">
            <div>
              <Label>Nombre</Label>
              <Input value={editando.nombre} maxLength={120}
                onChange={(e) => setEditando((p) => ({ ...p, nombre: e.target.value }))} data-testid="colab-cat-nombre" />
            </div>
            <div>
              <Label>{def.precio}</Label>
              <Input inputMode="decimal" value={editando.precio ?? ''}
                onChange={(e) => setEditando((p) => ({ ...p, precio: e.target.value }))} data-testid="colab-cat-precio" />
            </div>
            {cual === 'cirugias' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editando.sin_iva}
                  onChange={(e) => setEditando((p) => ({ ...p, sin_iva: e.target.checked }))} />
                Exenta de IVA (el precio va en la casilla «sin IVA»)
              </label>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setEditando(null)}><X className="w-4 h-4 mr-1" /> Cancelar</Button>
              <Button size="sm" onClick={guardar} disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="colab-cat-guardar">
                {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setEditando({ nombre: '', precio: '', sin_iva: false })}
            data-testid="colab-cat-nuevo">
            <Plus className="w-4 h-4 mr-1" /> Añadir a {def.titulo.toLowerCase()}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
